<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { toast } from '~/utils/sonner'

type ReleaseTarget = 'stable' | 'beta' | 'tutorial'
type VersionSortDirection = 'asc' | 'desc'
type VersionSortKey =
  | 'version'
  | 'build'
  | 'size'
  | 'uploadedAt'
  | 'installCount'
  | 'users'
  | 'launchCount'
  | 'trafficBytes'
  | 'lastSeenAt'
  | 'promoted'
type AndroidVersionRecord = client_versionsAttributes & {
  downloadUrl?: string
  releaseNotes?: string
  releaseNotesEn?: string
  minVersionCode?: number
  versionCode?: number
  forceUpdate?: boolean
  extra?: {
    r2_key?: string | null
    original_filename?: string | null
  }
}

const file = ref<File | null>(null)
const fileSha256 = ref('')
const versionName = ref('')
const versionCode = ref<number | null>(null)
const minVersionCode = ref<number>(0)
const forceUpdateEnabled = ref(false)
const releaseNotes = ref('')
const releaseNotesEn = ref('')
const uploading = ref(false)
const translatingReleaseNotes = ref(false)
const metricsLoading = ref(false)
const draggingApk = ref(false)
const uploadStatus = ref('')
const metricsError = ref('')
const editingVersion = ref<AndroidVersionRecord | null>(null)
const { translateText } = useTranslationTools()
const dayjs = useDayjs()
const metricWindowDays = ref(1)
const metricWindowOptions = [1, 7, 30, 90]
const versionMetrics = ref<Record<string, AndroidVersionMetrics>>({})
const versionSort = ref<{ key: VersionSortKey | null, direction: VersionSortDirection }>({
  key: null,
  direction: 'desc',
})
const state = ref<{
  channels: {
    stable: client_versionsAttributes | null
    beta: client_versionsAttributes | null
  }
  downloads: {
    tutorial: client_versionsAttributes | null
  }
  versions: client_versionsAttributes[]
}>({
  channels: { stable: null, beta: null },
  downloads: { tutorial: null },
  versions: [],
})

const isEditingExisting = computed(() => Boolean(editingVersion.value))
const canUpload = computed(() =>
  Boolean(
    versionName.value &&
    versionCode.value !== null &&
    fileSha256.value &&
    (file.value || editingVersion.value),
  ),
)

const versionRows = computed(() => sortVersionRows(state.value.versions.map((version, originalIndex) => ({
  version,
  metrics: metricsOf(version),
  originalIndex,
}))))

const digestHex = async (target: File) => {
  const buffer = await target.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

const parseApkFileName = (name: string) => {
  const baseName = name.replace(/\.apk$/i, '')
  const versionMatch = baseName.match(/(?:^|[-_\s.])v?(\d+(?:\.\d+){1,3}(?:[-_.]?(?:alpha|beta|rc)\d*)?)/i)
  const version = versionMatch?.[1]?.replace(/_/g, '-') || ''
  const afterVersion = versionMatch?.index !== undefined
    ? baseName.slice(versionMatch.index + versionMatch[0].length)
    : baseName
  const explicitBuild = baseName.match(/(?:build|buildCode|versionCode|vc|b)[-_\s.]?(\d{1,7})/i)
  const plusBuild = baseName.match(/\+\s*(\d{1,7})(?:[-_\s.]|$)/)
  const trailingBuild = afterVersion
    .match(/(?:^|[-_\s.])(\d{1,7})(?=$|[-_\s.])(?!\d)/g)
    ?.map((part) => Number(part.replace(/\D/g, '')))
    .find((num) => num > 0 && String(num).length < 8)
  const build = Number(explicitBuild?.[1] || plusBuild?.[1] || trailingBuild || 0) || null
  return { version, build }
}

function applyParsedFileName(showToast = true) {
  if (!file.value) return
  const parsed = parseApkFileName(file.value.name)
  versionName.value = parsed.version
  versionCode.value = parsed.build
  if (showToast) {
    toast({
      title: parsed.version || parsed.build ? '已从文件名解析' : '未解析到版本信息',
      description: `${parsed.version || '-'} / build ${parsed.build ?? '-'}`,
      variant: parsed.version || parsed.build ? undefined : 'destructive',
    })
  }
}

async function setSelectedApk(picked: File | null) {
  editingVersion.value = null
  file.value = picked
  fileSha256.value = ''
  if (!picked) return
  if (!picked.name.toLowerCase().endsWith('.apk')) {
    toast({
      title: '请选择 APK 文件',
      variant: 'destructive',
    })
    file.value = null
    return
  }
  applyParsedFileName(false)
  uploadStatus.value = '正在计算 SHA-256...'
  fileSha256.value = await digestHex(picked)
  uploadStatus.value = 'SHA-256 已计算'
}

async function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  await setSelectedApk(input.files?.[0] || null)
  if (!file.value) input.value = ''
}

async function onApkDrop(event: DragEvent) {
  draggingApk.value = false
  const picked = event.dataTransfer?.files?.[0] || null
  await setSelectedApk(picked)
}

async function refresh() {
  await Promise.all([
    refreshVersions(),
    refreshMetrics(),
  ])
}

async function refreshVersions() {
  try {
    state.value = await useAdmin.getAndroidUpdateVersions()
  } catch (error) {
    toast({
      title: '加载 Android 版本失败',
      description: errorText(error),
      variant: 'destructive',
    })
  }
}

async function refreshMetrics() {
  metricsLoading.value = true
  metricsError.value = ''
  try {
    const rows = await useAxiom().getAndroidVersionMetrics({
      durationDays: metricWindowDays.value,
      limit: 500,
    })
    versionMetrics.value = Object.fromEntries(rows.map((row) => [row.version, row]))
  } catch (error) {
    metricsError.value = errorText(error)
    toast({
      title: '加载 Android 指标失败',
      description: metricsError.value,
      variant: 'destructive',
    })
  } finally {
    metricsLoading.value = false
  }
}

async function setMetricWindow(days: number) {
  if (metricWindowDays.value === days) return
  metricWindowDays.value = days
  await refreshMetrics()
}

function metricWindowLabel(days: number) {
  return days === 1 ? '24 小时' : `${days} 天`
}

function toggleVersionSort(key: VersionSortKey) {
  if (versionSort.value.key === key) {
    versionSort.value.direction = versionSort.value.direction === 'asc' ? 'desc' : 'asc'
    return
  }
  versionSort.value = {
    key,
    direction: 'desc',
  }
}

function versionSortIcon(key: VersionSortKey) {
  if (versionSort.value.key !== key) return 'material-symbols:unfold-more'
  return versionSort.value.direction === 'asc' ? 'material-symbols:arrow-upward' : 'material-symbols:arrow-downward'
}

function sortVersionRows(
  rows: { version: client_versionsAttributes, metrics: AndroidVersionMetrics | null, originalIndex: number }[],
) {
  const { key, direction } = versionSort.value
  if (!key) return rows

  const directionMultiplier = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const result = compareVersionSortValues(
      versionSortValue(a, key),
      versionSortValue(b, key),
      directionMultiplier,
    )
    return result || a.originalIndex - b.originalIndex
  })
}

function compareVersionSortValues(
  a: string | number | null,
  b: string | number | null,
  directionMultiplier: number,
) {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  let result: number
  if (typeof a === 'string' || typeof b === 'string') {
    result = String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' })
  } else {
    result = a - b
  }
  return result * directionMultiplier
}

function versionSortValue(
  row: { version: client_versionsAttributes, metrics: AndroidVersionMetrics | null },
  key: VersionSortKey,
) {
  if (key === 'version') return String(row.version.version || '')
  if (key === 'build') return versionCodeOf(row.version)
  if (key === 'size') return sizeOf(row.version)
  if (key === 'uploadedAt') return versionUploadEpochOf(row.version)
  if (key === 'installCount') return row.metrics?.installCount ?? null
  if (key === 'users') return row.metrics?.activeUsers || row.metrics?.eventUserCount || null
  if (key === 'launchCount') return row.metrics?.launchCount ?? null
  if (key === 'trafficBytes') return row.metrics?.trafficBytes ?? null
  if (key === 'lastSeenAt') return row.metrics?.lastSeenAt ?? null
  return promotedSortValue(row.version)
}

function errorText(error: any) {
  return error?.data?.message || error?.data?.error || error?.message || JSON.stringify(error, null, 2)
}

function stripLeadingSpacesPerLine(text: string) {
  return text.replace(/^[ \t]+/gm, '')
}

async function uploadAndRegister() {
  if (!canUpload.value || versionCode.value === null) {
    toast({
      title: '信息不完整',
      description: isEditingExisting.value
        ? '需要 version、build 和 SHA-256。'
        : '需要 APK、version、build 和 SHA-256。',
      variant: 'destructive',
    })
    return
  }
  const editing = editingVersion.value
  const size = editing ? sizeOf(editing) : file.value?.size
  const confirmed = await useDialog().confirm({
    title: editing ? '保存 Android 版本' : '上传 Android APK',
    body: `版本 ${versionName.value} (build ${versionCode.value})\n大小 ${formatBytes(size)}\nSHA-256 ${fileSha256.value}`,
    confirmButtonText: editing ? '保存修改' : '上传并登记',
    cancelButtonText: '取消',
    confirmButtonVariant: 'warning',
  })
  if (confirmed !== 'confirm') return

  uploading.value = true
  try {
    let upload: {
      key: string
      publicUrl: string
      uploadUrl: string
      contentType: string
    } | null = null
    if (!editing) {
      if (!file.value) throw new Error('APK file is required')
      uploadStatus.value = '正在获取 R2 上传地址...'
      upload = await useAdmin.createAndroidUpdateUploadUrl({
        versionName: versionName.value,
        versionCode: versionCode.value,
        fileName: file.value.name,
        contentType: file.value.type || 'application/vnd.android.package-archive',
      })
      uploadStatus.value = `正在上传到 ${upload.key}...`
      const put = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': upload.contentType,
        },
        body: file.value,
      })
      if (!put.ok) {
        throw new Error(`R2 upload failed: HTTP ${put.status}`)
      }
    }
    uploadStatus.value = '正在登记版本...'
    releaseNotes.value = stripLeadingSpacesPerLine(releaseNotes.value)
    releaseNotesEn.value = stripLeadingSpacesPerLine(releaseNotesEn.value)
    const ret = await useAdmin.registerAndroidUpdateVersion({
      versionName: versionName.value,
      versionCode: versionCode.value,
      minVersionCode: forceUpdateEnabled.value ? minVersionCode.value || 0 : 0,
      releaseNotes: releaseNotes.value,
      releaseNotesEn: releaseNotesEn.value,
      sha256: fileSha256.value,
      size,
      key: upload?.key || editing?.extra?.r2_key || undefined,
      downloadUrl: upload?.publicUrl || downloadUrlOf(editing),
      fileName: file.value?.name || editing?.extra?.original_filename || undefined,
    })
    state.value = ret.state
    uploadStatus.value = editing ? '修改已保存' : '上传和登记完成'
    editingVersion.value = null
    toast({
      title: editing ? 'Android 版本已更新' : 'Android 版本已登记',
      description: `${versionName.value} (build ${versionCode.value})`,
    })
  } catch (error) {
    uploadStatus.value = '上传失败'
    toast({
      title: 'Android 版本上传失败',
      description: errorText(error),
      variant: 'destructive',
    })
  } finally {
    uploading.value = false
  }
}

async function translateReleaseNotesEn() {
  if (!releaseNotes.value.trim()) {
    toast({
      title: '没有可翻译内容',
      description: '先填写 Release Notes。',
      variant: 'destructive',
    })
    return
  }
  if (releaseNotesEn.value.trim()) {
    const confirmed = await useDialog().confirm({
      title: '覆盖 Release Notes EN？',
      body: '已有英文 Release Notes，继续翻译会覆盖当前内容。',
      confirmButtonText: '覆盖并翻译',
      cancelButtonText: '取消',
      confirmButtonVariant: 'warning',
    })
    if (confirmed !== 'confirm') return
  }

  translatingReleaseNotes.value = true
  try {
    const result = await translateText(releaseNotes.value, {
      customPrompt: `Translate these Android app release notes from Chinese to natural English.

Keep the same bullet/list structure.
Preserve version numbers, product names, technical terms, URLs, and code-like text.
Use concise app-store style wording.
Return ONLY the translated release notes without any explanation.`,
    })
    if (result.success && result.translatedText) {
      releaseNotesEn.value = result.translatedText
      toast({ title: 'Release Notes EN 已翻译' })
      return
    }
    throw new Error('Translation returned no text')
  } catch (error) {
    toast({
      title: '翻译失败',
      description: errorText(error),
      variant: 'destructive',
    })
  } finally {
    translatingReleaseNotes.value = false
  }
}

async function promote(target: ReleaseTarget, version: client_versionsAttributes) {
  const code = versionCodeOf(version)
  if (!code) return
  const targetLabel = releaseTargetLabel(target)
  const confirmed = await useDialog().confirm({
    title: `推送到 ${targetLabel}`,
    body: `当前 ${targetLabel} 将指向 ${version.version} (build ${code})。`,
    confirmButtonText: '确认推送',
    cancelButtonText: '取消',
    confirmButtonVariant: target === 'beta' ? 'warning' : 'default',
  })
  if (confirmed !== 'confirm') return
  try {
    const ret = await useAdmin.promoteAndroidUpdateVersion({ target, versionCode: code })
    state.value = ret.state
    toast({
      title: `${targetLabel} 已更新`,
      description: `${version.version} (build ${code})`,
    })
  } catch (error) {
    toast({
      title: `${targetLabel} 推送失败`,
      description: errorText(error),
      variant: 'destructive',
    })
  }
}

async function unpromote(target: ReleaseTarget) {
  const current = target === 'tutorial'
    ? state.value.downloads.tutorial
    : state.value.channels[target]
  if (!current) return
  const targetLabel = releaseTargetLabel(target)
  const confirmed = await useDialog().confirm({
    title: `撤下 ${targetLabel}`,
    body: `当前 ${targetLabel} 将不再指向任何版本。\n已登记版本和 APK 文件会保留，可之后重新推送。\n\n当前版本：${versionLabel(current)}`,
    confirmButtonText: '确认撤下',
    cancelButtonText: '取消',
    confirmButtonVariant: 'warning',
  })
  if (confirmed !== 'confirm') return
  try {
    const ret = await useAdmin.unpromoteAndroidUpdateVersion({ target })
    state.value = ret.state
    toast({
      title: `${targetLabel} 已撤下`,
      description: versionLabel(current),
    })
  } catch (error) {
    toast({
      title: `${targetLabel} 撤下失败`,
      description: errorText(error),
      variant: 'destructive',
    })
  }
}

function releaseTargetLabel(target: ReleaseTarget) {
  return target === 'tutorial' ? '教程下载' : target
}

function versionCodeOf(version?: client_versionsAttributes | null) {
  return Number((version as AndroidVersionRecord | null)?.versionCode ?? version?.version_code ?? 0)
}

function minVersionCodeOf(version?: AndroidVersionRecord | null) {
  return Number(version?.minVersionCode ?? version?.min_version_code ?? 0)
}

function sizeOf(version?: AndroidVersionRecord | null) {
  return Number(version?.size || 0)
}

function releaseNotesOf(version?: AndroidVersionRecord | null) {
  return String(version?.releaseNotes ?? version?.changelog ?? '')
}

function releaseNotesEnOf(version?: AndroidVersionRecord | null) {
  return String(version?.releaseNotesEn ?? version?.changelog_en ?? '')
}

function downloadUrlOf(version?: AndroidVersionRecord | null) {
  return String(version?.downloadUrl ?? version?.link ?? '')
}

function isPromoted(target: ReleaseTarget, version: client_versionsAttributes) {
  const current = target === 'tutorial'
    ? state.value.downloads.tutorial
    : state.value.channels[target]
  return versionCodeOf(current) === versionCodeOf(version)
}

function promotedSortValue(version: client_versionsAttributes) {
  return (isPromoted('tutorial', version) ? 1 : 0)
    + (isPromoted('stable', version) ? 2 : 0)
    + (isPromoted('beta', version) ? 4 : 0)
}

function versionLabel(version?: client_versionsAttributes | null) {
  if (!version) return '未设置'
  return `${version.version || '-'} · build ${versionCodeOf(version)}`
}

function metricVersionKeyOf(version?: client_versionsAttributes | null) {
  const name = String(version?.version || '').trim()
  if (!name) return ''
  if (name.includes('+')) return name
  const code = versionCodeOf(version)
  return code ? `${name}+${code}` : name
}

function metricsOf(version?: client_versionsAttributes | null) {
  const key = metricVersionKeyOf(version)
  return key ? versionMetrics.value[key] || null : null
}

function shortHash(hash?: string) {
  if (!hash) return '-'
  return hash.length > 6 ? `${hash.slice(0, 3)}...${hash.slice(-3)}` : hash
}

function formatMetricNumber(value?: number | null) {
  if (value === null || value === undefined) return '-'
  return Math.round(Number(value) || 0).toLocaleString('en-US')
}

function formatMetricBytes(bytes?: number | null) {
  if (bytes === null || bytes === undefined) return '-'
  return formatBytes(Number(bytes || 0))
}

function formatMetricTime(epochMs?: number | null) {
  if (!epochMs) return '-'
  return dayjs(epochMs).format('MM-DD HH:mm')
}

function trafficMetricTitle(metrics?: AndroidVersionMetrics | null) {
  if (!metrics) return '-'
  return `${formatMetricBytes(metrics.trafficUpBytes)} up / ${formatMetricBytes(metrics.trafficDownBytes)} down`
}

function versionUploadTimeOf(version?: client_versionsAttributes | null) {
  const value = version?.createdAt || version?.created_at
  if (!value) return '-'
  const date = dayjs(value)
  return date.isValid() ? date.format('MM-DD HH:mm') : '-'
}

function versionUploadEpochOf(version?: client_versionsAttributes | null) {
  const value = version?.createdAt || version?.created_at
  if (!value) return null
  const date = dayjs(value)
  return date.isValid() ? date.valueOf() : null
}

function formatBytes(bytes?: number) {
  const value = Number(bytes || 0)
  if (!value) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = value
  let unit = 0
  while (n >= 1024 && unit < units.length - 1) {
    n /= 1024
    unit += 1
  }
  return `${n.toFixed(unit === 0 ? 0 : 2)} ${units[unit]}`
}

function editVersion(version: client_versionsAttributes) {
  editingVersion.value = version
  file.value = null
  fileSha256.value = String(version.sha256 || '')
  versionName.value = String(version.version || '')
  versionCode.value = versionCodeOf(version)
  minVersionCode.value = minVersionCodeOf(version)
  forceUpdateEnabled.value = minVersionCode.value > 0 || Boolean(version.forceUpdate ?? version.force_update)
  releaseNotes.value = releaseNotesOf(version)
  releaseNotesEn.value = releaseNotesEnOf(version)
  uploadStatus.value = '正在编辑已登记版本'
}

function resetVersionDraft() {
  file.value = null
  fileSha256.value = ''
  versionName.value = ''
  versionCode.value = null
  minVersionCode.value = 0
  forceUpdateEnabled.value = false
  releaseNotes.value = ''
  releaseNotesEn.value = ''
  draggingApk.value = false
  uploadStatus.value = ''
}

function cancelEdit() {
  editingVersion.value = null
  resetVersionDraft()
}

onMounted(refresh)
</script>

<template lang="pug">
div(class="mx-auto w-full max-w-7xl space-y-8 px-2 py-6")
  div(class="flex flex-col gap-3 border-b pb-5 sm:flex-row sm:items-start sm:justify-between")
    div(class="space-y-1")
      h1(class="text-2xl font-semibold tracking-tight") Android 版本
      p(class="text-sm text-muted-foreground") 上传 APK、登记版本、选择 stable / beta 推送。
    Button(variant="outline" size="sm" class="w-fit" @click="refresh")
      Icon(name="material-symbols:refresh" class="mr-2 h-4 w-4")
      | 刷新

  div(class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]")
    Card(class="gap-0 py-0")
      CardHeader(class="border-b px-6 py-5")
        div(class="flex items-center justify-between gap-4")
          div
            CardTitle(class="text-base") {{ isEditingExisting ? '编辑已登记版本' : '新版本登记' }}
          div(class="flex items-center gap-2")
            Badge(v-if="isEditingExisting" variant="outline") 编辑中
            Badge(v-else-if="fileSha256" variant="secondary") hash ready
      CardContent(class="px-6 py-6")
        label(for="android-apk-input" :class="`flex min-h-32 items-center justify-between gap-4 rounded-md border border-dashed px-5 py-5 transition-colors ${isEditingExisting ? 'cursor-default border-border bg-muted/20' : 'cursor-pointer ' + (draggingApk ? 'border-primary bg-primary/10' : 'border-border bg-muted/20 hover:bg-muted/35')}`" @dragenter.prevent="draggingApk = !isEditingExisting" @dragover.prevent="draggingApk = !isEditingExisting" @dragleave.prevent="draggingApk = false" @drop.prevent="!isEditingExisting && onApkDrop($event)")
          Input#android-apk-input(class="sr-only" type="file" accept=".apk,application/vnd.android.package-archive" @change="onFileChange")
          div(class="min-w-0 space-y-2")
            p(class="text-sm font-medium") {{ isEditingExisting ? `编辑 ${versionName || '-'} build ${versionCode ?? '-'}` : (file?.name || '选择 PandaFan APK') }}
            p(class="text-xs text-muted-foreground") {{ isEditingExisting ? '复用已上传 APK，只修改登记信息' : (file ? `${formatBytes(file.size)} · ${uploadStatus || '等待上传'}` : '点击选择或拖入 .apk，选择后自动解析版本并计算 SHA-256') }}
          Icon(name="material-symbols:upload-file" class="h-6 w-6 shrink-0 text-muted-foreground")

        div(class="mt-7 grid gap-5 md:grid-cols-[minmax(0,1fr)_180px_auto]")
          label(class="grid gap-1.5 text-sm")
            span Version
            Input(v-model="versionName" placeholder="1.0.3")
          label(class="grid gap-1.5 text-sm")
            span Build
            Input(v-model.number="versionCode" type="number" min="0" placeholder="103" :disabled="isEditingExisting")
          label(class="flex items-end gap-2 pb-2 text-sm")
            input(v-model="forceUpdateEnabled" type="checkbox" class="h-4 w-4")
            span 强制自动更新？
        label(v-if="forceUpdateEnabled" class="mt-4 grid max-w-xs gap-1.5 text-sm")
          span Min Build
          Input(v-model.number="minVersionCode" type="number" min="0" placeholder="低于该 build 会强制更新")

        div(class="mt-8 grid gap-6 xl:grid-cols-2")
          label(class="grid gap-1.5 text-sm")
            span 中文
            Textarea(v-model="releaseNotes" class="min-h-36" rows="7" placeholder="- 修复...\n- 改进...")
          label(class="grid gap-1.5 text-sm")
            span(class="flex items-center justify-between gap-2")
              span English
              Button(
                type="button"
                variant="outline"
                size="sm"
                class="h-8"
                :disabled="translatingReleaseNotes || !releaseNotes.trim()"
                @click.prevent="translateReleaseNotesEn"
              )
                Icon(name="material-symbols:translate" class="mr-1 h-4 w-4")
                | {{ translatingReleaseNotes ? '翻译中...' : '自动翻译' }}
            Textarea(v-model="releaseNotesEn" class="min-h-36" rows="7" placeholder="- Fixed...\n- Improved...")

        div(class="flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between")
          div(class="min-w-0 text-xs text-muted-foreground")
            p(class="truncate font-mono" :title="fileSha256") SHA-256: {{ shortHash(fileSha256) }}
            p(class="truncate" :title="uploadStatus") {{ uploadStatus || '等待选择 APK' }}
          div(class="flex flex-wrap gap-2")
            Button(v-if="isEditingExisting" variant="outline" :disabled="uploading" @click="cancelEdit") 取消编辑
            Button(:disabled="!canUpload || uploading" @click="uploadAndRegister")
              Icon(name="material-symbols:upload-file" class="mr-2 h-4 w-4")
              | {{ uploading ? '处理中...' : (isEditingExisting ? '保存修改' : '上传并登记') }}

    div(class="space-y-6")
      Card(class="gap-0 py-0")
        CardHeader(class="border-b px-5 py-4")
          CardTitle(class="text-base") 当前推送
        CardContent(class="divide-y px-5 py-0")
          div(class="py-4")
            div(class="mb-2 flex items-center justify-between")
              Badge(variant="outline") 教程下载
              div(class="flex items-center gap-2")
                span(class="text-xs text-muted-foreground") 教程页
                Button(v-if="state.downloads.tutorial" size="sm" variant="ghost" class="h-7 px-2 text-xs" @click="unpromote('tutorial')") 撤下
            p(class="truncate text-sm font-medium" :title="versionLabel(state.downloads.tutorial)") {{ versionLabel(state.downloads.tutorial) }}
          div(class="py-4")
            div(class="mb-2 flex items-center justify-between")
              Badge(variant="default") stable
              div(class="flex items-center gap-2")
                span(class="text-xs text-muted-foreground") 正式
                Button(v-if="state.channels.stable" size="sm" variant="ghost" class="h-7 px-2 text-xs" @click="unpromote('stable')") 撤下
            p(class="truncate text-sm font-medium" :title="versionLabel(state.channels.stable)") {{ versionLabel(state.channels.stable) }}
          div(class="py-4")
            div(class="mb-2 flex items-center justify-between")
              Badge(variant="secondary") beta
              div(class="flex items-center gap-2")
                span(class="text-xs text-muted-foreground") 测试
                Button(v-if="state.channels.beta" size="sm" variant="ghost" class="h-7 px-2 text-xs" @click="unpromote('beta')") 撤下
            p(class="truncate text-sm font-medium" :title="versionLabel(state.channels.beta)") {{ versionLabel(state.channels.beta) }}

      div(class="mt-6 space-y-4 rounded-md border bg-muted/20 p-5 text-sm")
        p(class="font-medium") 发布顺序
        ol(class="space-y-2 text-xs text-muted-foreground")
          li 1. 上传 APK 并登记版本信息。
          li 2. 教程页下载、stable、beta 分别选择版本。
          li 3. App 内更新只读取 stable / beta。

  Card(class="mt-2 gap-0 py-0")
    CardHeader(class="border-b px-6 py-5")
      div(class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between")
        div
          CardTitle(class="text-base") 已登记版本
          p(class="mt-1 text-xs text-muted-foreground") {{ state.versions.length }} 个版本 · 指标最近 {{ metricWindowLabel(metricWindowDays) }}
        div(class="flex flex-wrap items-center gap-2")
          div(class="inline-flex overflow-hidden rounded-md border bg-background")
            Button(
              v-for="days in metricWindowOptions"
              :key="days"
              size="sm"
              :variant="metricWindowDays === days ? 'secondary' : 'ghost'"
              class="h-8 rounded-none border-0 px-3"
              :disabled="metricsLoading"
              @click="setMetricWindow(days)"
            ) {{ metricWindowLabel(days) }}
          Button(size="sm" variant="outline" class="h-8" :disabled="metricsLoading" @click="refreshMetrics")
            Icon(name="material-symbols:refresh" class="mr-1 h-4 w-4" :class="{ 'animate-spin': metricsLoading }")
            | 指标
    CardContent(class="px-6 py-0")
      div(v-if="metricsError" class="border-b py-3 text-sm text-destructive") {{ metricsError }}
      div(class="overflow-x-auto")
        table(class="w-full text-sm")
          thead
            tr(class="border-b text-left text-muted-foreground")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('version')")
                  span 版本
                  Icon(:name="versionSortIcon('version')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'version' }")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('build')")
                  span build
                  Icon(:name="versionSortIcon('build')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'build' }")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('size')")
                  span 大小
                  Icon(:name="versionSortIcon('size')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'size' }")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('uploadedAt')")
                  span 上传时间
                  Icon(:name="versionSortIcon('uploadedAt')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'uploadedAt' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('installCount')")
                  span 安装设备
                  Icon(:name="versionSortIcon('installCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'installCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('users')")
                  span 用户
                  Icon(:name="versionSortIcon('users')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'users' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('launchCount')")
                  span 服务启动
                  Icon(:name="versionSortIcon('launchCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'launchCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('trafficBytes')")
                  span 流量总量
                  Icon(:name="versionSortIcon('trafficBytes')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'trafficBytes' }")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('lastSeenAt')")
                  span 最新上报
                  Icon(:name="versionSortIcon('lastSeenAt')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'lastSeenAt' }")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleVersionSort('promoted')")
                  span 当前
                  Icon(:name="versionSortIcon('promoted')" class="h-3.5 w-3.5" :class="{ 'opacity-40': versionSort.key !== 'promoted' }")
              th(class="py-3 pr-0 text-right font-medium") 操作
          tbody
            tr(v-for="row in versionRows" :key="versionCodeOf(row.version)" class="border-b last:border-b-0")
              td(class="py-3 pr-4 font-medium") {{ row.version.version }}
              td(class="py-3 pr-4") {{ versionCodeOf(row.version) }}
              td(class="py-3 pr-4") {{ formatBytes(sizeOf(row.version)) }}
              td(class="py-3 pr-4 whitespace-nowrap") {{ versionUploadTimeOf(row.version) }}
              td(class="py-3 pr-4 text-right tabular-nums" :title="metricVersionKeyOf(row.version)") {{ formatMetricNumber(row.metrics?.installCount) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.metrics?.activeUsers || row.metrics?.eventUserCount) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.metrics?.launchCount) }}
              td(class="py-3 pr-4 text-right tabular-nums" :title="trafficMetricTitle(row.metrics)") {{ formatMetricBytes(row.metrics?.trafficBytes) }}
              td(class="py-3 pr-4 whitespace-nowrap") {{ formatMetricTime(row.metrics?.lastSeenAt) }}
              td(class="py-3 pr-4")
                div(class="flex gap-1")
                  Badge(v-if="isPromoted('tutorial', row.version)" variant="outline") 教程
                  Badge(v-if="isPromoted('stable', row.version)" variant="default") stable
                  Badge(v-if="isPromoted('beta', row.version)" variant="secondary") beta
              td(class="py-3 pr-0")
                div(class="flex justify-end gap-2")
                  Button(size="sm" variant="ghost" @click="editVersion(row.version)") 编辑
                  Button(size="sm" variant="outline" :disabled="isPromoted('tutorial', row.version)" @click="promote('tutorial', row.version)") 教程下载
                  Button(size="sm" variant="outline" :disabled="isPromoted('stable', row.version)" @click="promote('stable', row.version)") 推 Stable
                  Button(size="sm" variant="outline" :disabled="isPromoted('beta', row.version)" @click="promote('beta', row.version)") 推 Beta
            tr(v-if="!state.versions.length")
              td(class="py-10 text-center text-muted-foreground" colspan="11") 暂无 Android 版本
</template>
